import { useEffect, useMemo, useRef, useState } from "react";
import { PlusCircle, FileSpreadsheet, RefreshCw, Filter, ArrowUp, ArrowDown, ChevronRight, Columns3, ChevronUp, ChevronDown, RotateCcw, XCircle, FolderOpen, Undo2, Printer } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fmtNum } from "@/lib/format";
import { printElement } from "@/lib/print";

const val = (c, r) => (c.get ? c.get(r) : r[c.key]);
const txt = (v) => (v == null ? "" : String(v));

export function exportCsv(name, cols, rows) {
  const esc = (v) => `"${txt(v).replace(/"/g, '""')}"`;
  const lines = ["sep=;", cols.map((c) => esc(c.label)).join(";"), ...rows.map((r) => cols.map((c) => esc(val(c, r))).join(";"))];
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

function useLayout(key, columns) {
  const [lay, setLay] = useState(() => ({ order: [], hidden: [], widths: {}, ...JSON.parse(localStorage.getItem(key) || "{}") }));
  useEffect(() => { localStorage.setItem(key, JSON.stringify(lay)); }, [key, lay]);
  const keys = columns.map((c) => c.key);
  const order = [...lay.order.filter((k) => keys.includes(k)), ...keys.filter((k) => !lay.order.includes(k))];
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const move = (k, to) => { const o = order.filter((x) => x !== k); o.splice(Math.max(0, Math.min(to, o.length)), 0, k); setLay((l) => ({ ...l, order: o })); };
  return {
    order, all: order.map((k) => byKey[k]), cols: order.filter((k) => !lay.hidden.includes(k)).map((k) => byKey[k]), hidden: lay.hidden, widths: lay.widths, move,
    toggle: (k) => setLay((l) => ({ ...l, hidden: l.hidden.includes(k) ? l.hidden.filter((x) => x !== k) : [...l.hidden, k] })),
    setWidth: (k, w) => setLay((l) => ({ ...l, widths: { ...l.widths, [k]: w } })),
    reset: () => setLay({ order: [], hidden: [], widths: {} }),
  };
}

function ColumnsMenu({ L, testid }) {
  const { t } = useApp();
  return (
    <div className="absolute z-30 top-full mt-1 start-0 w-64 rounded-lg border border-[#CCCCCC] bg-white shadow-xl p-2 space-y-1" data-testid={`${testid}-columns-menu`}>
      {L.all.map((c, i) => (
        <div key={c.key} className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={!L.hidden.includes(c.key)} onChange={() => L.toggle(c.key)} className="w-4 h-4" data-testid={`${testid}-col-toggle-${c.key}`} />
          <span className="flex-1 truncate">{c.label}</span>
          <button type="button" onClick={() => L.move(c.key, i - 1)} disabled={i === 0} className="w-6 h-6 rounded hover:bg-[#EEF5FB] grid place-items-center disabled:opacity-30" data-testid={`${testid}-col-up-${c.key}`}><ChevronUp className="w-4 h-4" /></button>
          <button type="button" onClick={() => L.move(c.key, i + 1)} disabled={i === L.all.length - 1} className="w-6 h-6 rounded hover:bg-[#EEF5FB] grid place-items-center disabled:opacity-30" data-testid={`${testid}-col-down-${c.key}`}><ChevronDown className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={L.reset} className="w-full h-8 mt-1 rounded bg-[#EEF5FB] text-xs font-bold flex items-center justify-center gap-1" data-testid={`${testid}-columns-reset`}><RotateCcw className="w-3.5 h-3.5" />{t("reset_layout")}</button>
    </div>
  );
}

function RecordToolbar({ testid, onAdd, onOpen, onDelete, onUndo, onExport, onPrint, onReload, showF, setShowF, menu, setMenu, L, selected, t }) {
  const need = (fn) => () => (selected ? fn(selected) : toast.info(t("tb_select_row")));
  const B = ({ icon: Icon, color, label, onClick, id, disabled, on }) => (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={`${testid}-${id}`} title={label}
      className={`h-9 px-2 rounded-sm border flex items-center gap-1.5 text-[12px] text-[#222222] transition-colors disabled:opacity-35 ${on ? "bg-[#DDEEFF] border-[#9CC3E8]" : "border-transparent hover:bg-[#E5F1FB] hover:border-[#CCE4F7]"}`}>
      <Icon className="w-5 h-5" color={color} strokeWidth={1.8} />{label}
    </button>
  );
  const Sep = () => <span className="w-px h-6 bg-[#CCCCCC] mx-0.5" />;
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-[#CCCCCC] bg-[#F7F7F7]" data-testid={`${testid}-toolbar`}>
      <B icon={PlusCircle} color="#1F9D55" label={t("tb_new")} onClick={onAdd} id="add" disabled={!onAdd} />
      <B icon={FolderOpen} color="#E3A008" label={t("tb_open")} onClick={need(onOpen)} id="open" disabled={!onOpen} />
      <B icon={XCircle} color="#C7353B" label={t("tb_delete")} onClick={need(onDelete)} id="delete" disabled={!onDelete} />
      <B icon={Undo2} color="#2B79C2" label={t("tb_undo")} onClick={onUndo} id="undo" />
      <Sep />
      <B icon={Printer} color="#5F6B76" label={t("tb_print")} onClick={onPrint} id="print" />
      <B icon={FileSpreadsheet} color="#1F7A45" label="Excel" onClick={onExport} id="export" />
      <B icon={RefreshCw} color="#3498DB" label={t("load")} onClick={onReload} id="reload" />
      <Sep />
      <B icon={Filter} color="#5F6B76" label={t("filters")} onClick={() => setShowF((s) => !s)} id="filters-toggle" on={showF} />
      <div className="relative">
        <B icon={Columns3} color="#5F6B76" label={t("columns")} onClick={() => setMenu((m) => !m)} id="columns-btn" on={menu} />
        {menu && <ColumnsMenu L={L} testid={testid} />}
      </div>
    </div>
  );
}

export default function DataGrid({ columns, rows, dateField, onRowClick, onAdd, onDelete, name = "export", testid = "grid", actions, defaultSort }) {
  const { t, syncNow, staff } = useApp();
  const L = useLayout(`grid_${staff?.id || "anon"}_${testid}`, columns);
  const cols = L.cols;
  const [menu, setMenu] = useState(false);
  const drag = useRef(null);
  const tableRef = useRef(null);
  const onPrint = () => { const h1 = document.querySelector("main h1, h1"); printElement(tableRef.current, { landscape: cols.length > 6, title: name, heading: h1?.innerText?.trim() || (t(`nav_${name}`) !== `nav_${name}` ? t(`nav_${name}`) : t(name) !== name ? t(name) : name) }); };
  const resizing = useRef(false);
  const startResize = (e, k) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = true;
    const th = e.currentTarget.parentElement;
    const x0 = e.clientX;
    const w0 = th.getBoundingClientRect().width;
    const rtl = document.documentElement.dir === "rtl";
    const mv = (ev) => L.setWidth(k, Math.max(50, Math.round(w0 + (rtl ? x0 - ev.clientX : ev.clientX - x0))));
    const up = () => { resizing.current = false; window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };
  const [sort, setSort] = useState(defaultSort || null);
  const [filters, setFilters] = useState({});
  const [showF, setShowF] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sel, setSel] = useState(null);
  const shown = useMemo(() => {
    let r = rows.filter((row) => columns.every((c) => !filters[c.key] || txt(val(c, row)).toLowerCase().includes(filters[c.key].toLowerCase())));
    if (dateField && from) r = r.filter((row) => (row[dateField] || "").slice(0, 10) >= from);
    if (dateField && to) r = r.filter((row) => (row[dateField] || "").slice(0, 10) <= to);
    if (sort) {
      const c = columns.find((x) => x.key === sort.key);
      r = [...r].sort((a, b) => { const x = val(c, a); const y = val(c, b); const d = typeof x === "number" && typeof y === "number" ? x - y : txt(x).localeCompare(txt(y)); return sort.dir * d; });
    }
    return r;
  }, [rows, columns, filters, sort, dateField, from, to]);
  const sums = cols.filter((c) => c.sum).map((c) => [c, shown.reduce((s, r) => s + (Number(val(c, r)) || 0), 0)]);
  const toggleSort = (k) => setSort((s) => (s?.key === k ? (s.dir === 1 ? { key: k, dir: -1 } : null) : { key: k, dir: 1 }));
  const selected = shown.find((r) => r.id === sel) || null;
  const undo = () => { setSel(null); setFilters({}); setFrom(""); setTo(""); setSort(defaultSort || null); setShowF(false); };
  return (
    <div className="rounded-lg border border-[#CCCCCC] bg-white overflow-hidden" data-testid={testid}>
      <RecordToolbar testid={testid} t={t} L={L} onAdd={onAdd} onOpen={onRowClick} onDelete={onDelete} onUndo={undo} onPrint={onPrint} selected={selected}
        onExport={() => exportCsv(name, cols, shown)} onReload={syncNow} showF={showF} setShowF={setShowF} menu={menu} setMenu={setMenu} />
      {dateField && (
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-[#CCCCCC] bg-[#F0F0F0]">
          <div className="flex items-center gap-1.5 ms-auto text-xs" data-testid={`${testid}-date-range`}>
            <span className="font-semibold text-muted-foreground">{t("date_range")}</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded border border-[#CCCCCC] px-1.5" data-testid={`${testid}-from`} />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded border border-[#CCCCCC] px-1.5" data-testid={`${testid}-to`} />
          </div>
      </div>
      )}
      <div className="overflow-auto max-h-[calc(100vh-330px)]">
        <table ref={tableRef} className="w-full text-[13px] border-collapse">
          <thead className="sticky top-0 z-10 bg-[#E8E8E8]">
            <tr>
              <th className="w-6 border-b border-[#CCCCCC]" />
              {cols.map((c) => (
                <th key={c.key} draggable onDragStart={(e) => { if (resizing.current) { e.preventDefault(); return; } drag.current = c.key; }} onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (drag.current && drag.current !== c.key) L.move(drag.current, L.order.indexOf(c.key)); drag.current = null; }}
                  style={L.widths[c.key] ? { width: L.widths[c.key], minWidth: L.widths[c.key], maxWidth: L.widths[c.key] } : undefined}
                  className={`relative border-b border-e border-[#DDDDDD] px-2 h-8 font-semibold text-[#334] whitespace-nowrap cursor-grab ${c.align === "end" ? "text-end" : "text-start"}`} data-testid={`${testid}-th-${c.key}`}>
                  <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 max-w-full truncate" data-testid={`${testid}-sort-${c.key}`}>
                    {c.label}{sort?.key === c.key && (sort.dir === 1 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                  </button>
                  <span onPointerDown={(e) => startResize(e, c.key)} className="absolute top-0 end-0 w-1.5 h-full cursor-col-resize hover:bg-[#2B79C2]/40" data-testid={`${testid}-resize-${c.key}`} />
                </th>
              ))}
              {actions && <th className="border-b border-[#CCCCCC]" />}
            </tr>
            {showF && (
              <tr>
                <th className="border-b border-[#CCCCCC]" />
                {cols.map((c) => (
                  <th key={c.key} className="border-b border-e border-[#DDDDDD] p-1">
                    <input value={filters[c.key] || ""} onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })} className="w-full min-w-16 h-7 rounded border border-[#CCCCCC] px-1.5 font-normal" data-testid={`${testid}-filter-${c.key}`} />
                  </th>
                ))}
                {actions && <th className="border-b border-[#CCCCCC]" />}
              </tr>
            )}
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={r.id || i} onClick={() => { setSel(r.id); onRowClick?.(r); }} data-testid={`${testid}-row`}
                className={`cursor-pointer ${sel === r.id ? "bg-[#DDEEFF]" : i % 2 ? "bg-[#FAFAFA]" : "bg-white"} hover:bg-[#EEF5FB]`}>
                <td className="text-[#2B79C2] px-1">{sel === r.id && <ChevronRight className="w-4 h-4" />}</td>
                {cols.map((c) => (
                  <td key={c.key} style={L.widths[c.key] ? { maxWidth: L.widths[c.key] } : undefined} className={`px-2 h-8 border-e border-[#E8E8E8] whitespace-nowrap max-w-[280px] truncate ${c.align === "end" ? "text-end tabular-nums" : ""}`}>
                    {c.render ? c.render(r) : typeof val(c, r) === "number" ? fmtNum(val(c, r)) : txt(val(c, r))}
                  </td>
                ))}
                {actions && <td className="px-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>{actions(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <div className="p-8 text-center text-muted-foreground" data-testid={`${testid}-empty`}>{t("no_results")}</div>}
      </div>
      <div className="flex flex-wrap justify-end gap-4 px-3 h-8 items-center border-t border-[#CCCCCC] bg-[#F0F0F0] text-xs font-semibold text-[#333333]" data-testid={`${testid}-status`}>
        {sums.map(([c, s]) => <span key={c.key}>{c.label}: {fmtNum(s)}</span>)}
        <span data-testid={`${testid}-count`}>{shown.length} {t("records")}</span>
      </div>
    </div>
  );
}
